<template>
  <b-modal
    :modelValue="modelValue"
    dialog-class="rates-report-dialog"
    body-class="pt-0"
    footer-class="d-flex justify-content-between align-items-center"
    :no-close-on-backdrop="true"
    @update:modelValue="$emit('update:modelValue', $event)"
  >
    <template #title>
      <div>
        <div>{{ helper.trans('rates_management.report.title') }}</div>
        <small v-if="subtitle" class="text-muted fw-normal">{{ subtitle }}</small>
      </div>
    </template>

    <template #default>
      <div v-if="report" class="mt-3">
        <div v-if="drift" class="alert alert-warning py-2 px-3 mb-3">
          <i class="fa fa-triangle-exclamation me-2"></i>{{ helper.trans('rates_management.report.drift') }}
        </div>

        <div class="row row-cols-2 row-cols-md-5 g-2 mb-3">
          <div class="col">
            <div class="border rounded p-2 text-center h-100">
              <div class="fs-4 lh-1">{{ report.summary.matched }}</div>
              <small class="text-muted">{{ helper.trans('rates_management.report.matched') }}</small>
            </div>
          </div>
          <div class="col">
            <div class="border border-primary rounded p-2 text-center h-100">
              <div class="fs-4 lh-1 text-primary">{{ report.summary.changed }}</div>
              <small class="text-muted">{{ helper.trans('rates_management.report.will_change') }}</small>
            </div>
          </div>
          <div class="col">
            <div class="border rounded p-2 text-center h-100">
              <div class="fs-4 lh-1">{{ report.summary.unchanged }}</div>
              <small class="text-muted">{{ helper.trans('rates_management.report.unchanged') }}</small>
            </div>
          </div>
          <div class="col">
            <div class="border rounded p-2 text-center h-100">
              <div class="fs-4 lh-1" :class="{'text-muted': report.summary.skipped === 0}">{{ report.summary.skipped }}</div>
              <small class="text-muted">{{ helper.trans('rates_management.report.skipped') }}</small>
            </div>
          </div>
          <div class="col">
            <div class="border rounded p-2 text-center h-100">
              <div class="fs-4 lh-1">{{ report.summary.tradersChanged }}</div>
              <small class="text-muted">{{ helper.trans('rates_management.report.traders_changed') }}</small>
            </div>
          </div>
        </div>

        <div class="mb-3">
          <div class="small text-muted mb-1 text-capitalize-first">{{ helper.trans('rates_management.report.warnings') }}</div>
          <div v-if="report.summary.warnings.length === 0" class="text-success small">
            <i class="fa fa-circle-check me-1"></i>{{ helper.trans('rates_management.report.no_warnings') }}
          </div>
          <div v-else class="d-flex flex-wrap gap-2">
            <button
              v-for="warning in report.summary.warnings"
              :key="warning.code"
              type="button"
              class="btn btn-sm text-nowrap"
              :class="chipClass(warning, warningCodeFilter === warning.code)"
              :title="warningHint(warning.code, null)"
              @click="toggleWarningFilter(warning.code)"
            >
              {{ helper.trans(`rates_management.warning.${warning.code}`) }}
              <span class="badge bg-light text-dark ms-1">{{ warning.count }}</span>
              <span v-if="warning.isEstimate" class="badge bg-secondary ms-1">{{ helper.trans('rates_management.report.estimate') }}</span>
            </button>
          </div>
          <div v-if="hasEstimates" class="small text-muted mt-1">{{ helper.trans('rates_management.report.estimate_note') }}</div>
        </div>

        <details v-if="report.summary.groups.length > 1" class="mb-3">
          <summary class="small text-muted text-capitalize-first">{{ helper.trans('rates_management.report.groups') }}</summary>
          <div class="d-flex flex-wrap gap-2 mt-2">
            <span v-for="group in report.summary.groups" :key="group.trafficGroupId" class="border rounded px-2 py-1 small">
              {{ group.trafficGroupName }}:
              <b>{{ helper.trans('rates_management.report.group_stats', {changed: group.changed, matched: group.matched}) }}</b>
            </span>
          </div>
        </details>

        <div class="row g-2 align-items-center mb-2">
          <div class="col-auto">
            <div class="btn-group btn-group-sm" role="group">
              <button
                v-for="option in statusFilters"
                :key="option"
                type="button"
                class="btn"
                :class="statusFilter === option ? 'btn-primary' : 'btn-outline-secondary'"
                @click="statusFilter = option"
              >{{ helper.trans(`rates_management.report.filter_${option}`) }}</button>
            </div>
          </div>
          <div class="col-12 col-md-4 col-lg-3">
            <input type="search" class="form-control form-control-sm" v-model="search" :placeholder="helper.trans('rates_management.report.search')">
          </div>
          <div class="col text-end small text-muted">
            {{ helper.trans('rates_management.report.filtered_from', {shown: filteredRows.length, total: report.rows.length}) }}
          </div>
        </div>

        <div class="overflow-auto border rounded" style="max-height: 42vh">
          <table class="table table-sm table-hover align-middle small mb-0">
            <thead class="position-sticky top-0 bg-body" style="z-index: 1">
              <tr>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_trader') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_group') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_status') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_change') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_fee') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_cuts') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_total') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_cap') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_store_min') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('rates_management.report.col_warnings') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredRows.length === 0">
                <td colspan="10" class="text-center text-muted py-4">{{ helper.trans('rates_management.report.no_rows') }}</td>
              </tr>
              <tr v-for="row in filteredRows" :key="`${row.traderId}_${row.trafficGroupId}`" :class="{'text-muted': row.status !== 'changed'}">
                <td class="text-nowrap">
                  {{ row.traderName }}
                  <span class="text-muted small">#{{ row.traderId }}</span>
                  <span v-if="row.isProvider" class="badge bg-info text-dark ms-1">{{ helper.trans('rates_management.report.provider') }}</span>
                </td>
                <td class="text-nowrap">
                  {{ row.trafficGroupName }}
                  <span v-if="!row.enabled" class="badge bg-secondary ms-1">{{ helper.trans('rates_management.report.disabled') }}</span>
                </td>
                <td>
                  <span class="badge" :class="statusBadgeClass(row.status)" :title="row.skipReason ? helper.trans(`rates_management.skip_reason.${row.skipReason}`) : ''">
                    {{ helper.trans(`rates_management.status.${row.status}`) }}
                  </span>
                  <div v-if="row.skipReason" class="small text-muted text-wrap" style="max-width: 190px">{{ helper.trans(`rates_management.skip_reason.${row.skipReason}`) }}</div>
                </td>
                <td class="text-nowrap">
                  <template v-if="row.oldValue !== null && row.newValue !== null">
                    <span class="text-muted">{{ row.oldValue }}%</span>
                    <span class="mx-1" :class="deltaClass(row)">{{ deltaArrow(row) }}</span>
                    <b :class="deltaClass(row)">{{ row.newValue }}%</b>
                    <span v-if="row.cutIndex !== null" class="badge bg-light text-dark border ms-1">L{{ row.cutIndex + 1 }}</span>
                  </template>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-nowrap">{{ row.fee }}%</td>
                <td class="text-nowrap">
                  <span v-for="(cut, index) in row.cuts" :key="index">
                    <span v-if="index > 0" class="text-muted"> / </span>
                    <span :class="{'fw-bold text-primary': row.cutIndex === index}">{{ cut }}</span>
                  </span>
                </td>
                <td class="text-nowrap" :class="{'text-danger fw-bold': isOverLimit(row)}">{{ row.total }}%</td>
                <td class="text-nowrap">{{ row.teamLeadCap === null ? '-' : row.teamLeadCap + '%' }}</td>
                <td class="text-nowrap">{{ row.storeMin === null ? '-' : row.storeMin + '%' }}</td>
                <td style="min-width: 230px">
                  <div class="d-flex flex-wrap gap-1">
                    <span
                      v-for="warning in row.warnings"
                      :key="warning.code"
                      class="badge rounded-pill text-wrap text-start"
                      :class="warning.severity === 'warning' ? 'bg-warning text-dark' : 'bg-secondary'"
                      :title="warningHint(warning.code, warning.limit)"
                    >
                      {{ helper.trans(`rates_management.warning.${warning.code}`) }}<span v-if="warning.isEstimate"> ~</span>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="small text-muted me-3">
        <template v-if="report && report.summary.changed > 0">
          {{ helper.trans('rates_management.report.will_apply', {changed: report.summary.changed, traders: report.summary.tradersChanged}) }}
        </template>
        <template v-else-if="report">{{ helper.trans('rates_management.report.nothing_to_apply') }}</template>
      </div>
      <div class="text-nowrap">
        <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first me-2" :disabled="applying" @click="$emit('update:modelValue', false)">
          {{ helper.trans('rates_management.report.cancel') }}
        </button>
        <button type="button" class="btn btn-primary btn-sm text-capitalize-first" :disabled="applying || !report || report.summary.changed === 0" @click="$emit('confirm')">
          <span v-if="applying" class="spinner-border spinner-border-sm me-1"></span>
          {{ helper.trans('rates_management.report.confirm') }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, ref, watch} from 'vue'
import {helper} from "@/service/CommonHelper";
import TraderRatesBulkReportDTO from "@/models/TraderRatesBulkReportDTO";
import TraderRatesBulkRowDTO from "@/models/TraderRatesBulkRowDTO";
import {TraderRatesBulkWarningCountDTO} from "@/models/TraderRatesBulkSummaryDTO";
import TraderRatesBulkRequestDTO from "@/models/request/TraderRatesBulkRequestDTO";
import {Direction} from "@/models/enum/Direction";
import {TraderRatesBulkTarget} from "@/models/enum/TraderRatesBulkTarget";

type StatusFilter = 'all' | 'changed' | 'unchanged' | 'skipped' | 'warnings'

const MODE_LABEL_KEYS: Record<string, string> = {
  RATE_UP_ON: 'rate_change_modes.rate_up_on',
  RATE_DOWN_ON: 'rate_change_modes.rate_down_on',
  RATE_UP_UNTIL: 'rate_change_modes.rate_up_until',
  RATE_DOWN_UNTIL: 'rate_change_modes.rate_down_until',
  RATE_SET_ALL: 'rate_change_modes.rate_set_all',
}

export default defineComponent({
  name: 'TraderRatesBulkReportModal',
  emits: ['update:modelValue', 'confirm'],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    report: {
      type: Object as PropType<TraderRatesBulkReportDTO | null>,
      default: null
    },
    request: {
      type: Object as PropType<TraderRatesBulkRequestDTO | null>,
      default: null
    },
    drift: {
      type: Boolean,
      default: false
    },
    applying: {
      type: Boolean,
      default: false
    },
  },
  setup(props) {
    const statusFilters: StatusFilter[] = ['all', 'changed', 'warnings', 'unchanged', 'skipped']
    const statusFilter = ref<StatusFilter>('all')
    const warningCodeFilter = ref<string | null>(null)
    const search = ref('')

    watch(() => props.report?.previewHash, () => {
      statusFilter.value = 'all'
      warningCodeFilter.value = null
      search.value = ''
    })

    const subtitle = computed((): string => {
      if (!props.request) {
        return ''
      }

      return helper.trans('rates_management.report.subtitle', {
        direction: helper.trans(props.request.direction === Direction.PAY_IN ? 'rates_management.direction_in' : 'rates_management.direction_out'),
        target: helper.trans(props.request.target === TraderRatesBulkTarget.TRADER_FEE ? 'rates_management.target_trader_fee' : 'rates_management.target_team_lead_cut'),
        mode: helper.trans(MODE_LABEL_KEYS[props.request.mode]).toLowerCase(),
        value: props.request.value,
      })
    })

    const hasEstimates = computed((): boolean => {
      return !!props.report?.summary.warnings.some(warning => warning.isEstimate)
    })

    const rank = (row: TraderRatesBulkRowDTO): number => {
      if (row.warnings.some(warning => warning.severity === 'warning')) {
        return 0
      }

      return {changed: 1, unchanged: 2, skipped: 3}[row.status]
    }

    const sortedRows = computed((): TraderRatesBulkRowDTO[] => {
      const rows = props.report?.rows ?? []

      return rows
        .map((row, index) => ({row, index}))
        .sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index)
        .map(item => item.row)
    })

    const filteredRows = computed((): TraderRatesBulkRowDTO[] => {
      const query = search.value.trim().toLowerCase()

      return sortedRows.value.filter(row => {
        if (statusFilter.value === 'warnings' && row.warnings.length === 0) {
          return false
        }

        if (['changed', 'unchanged', 'skipped'].includes(statusFilter.value) && row.status !== statusFilter.value) {
          return false
        }

        if (warningCodeFilter.value !== null && !row.warnings.some(warning => warning.code === warningCodeFilter.value)) {
          return false
        }

        if (query === '') {
          return true
        }

        return row.traderName.toLowerCase().includes(query)
          || row.trafficGroupName.toLowerCase().includes(query)
          || String(row.traderId) === query
      })
    })

    const toggleWarningFilter = (code: string): void => {
      warningCodeFilter.value = warningCodeFilter.value === code ? null : code
    }

    const chipClass = (warning: TraderRatesBulkWarningCountDTO, active: boolean): string => {
      const base = warning.severity === 'warning' ? 'btn-outline-warning' : 'btn-outline-secondary'

      return active ? base.replace('outline-', '') : base
    }

    const statusBadgeClass = (status: string): string => {
      return {changed: 'bg-primary', unchanged: 'bg-secondary', skipped: 'bg-dark'}[status] ?? 'bg-secondary'
    }

    const delta = (row: TraderRatesBulkRowDTO): number => {
      return parseFloat(row.newValue ?? '0') - parseFloat(row.oldValue ?? '0')
    }

    const deltaArrow = (row: TraderRatesBulkRowDTO): string => {
      return delta(row) > 0 ? '↑' : (delta(row) < 0 ? '↓' : '=')
    }

    const deltaClass = (row: TraderRatesBulkRowDTO): string => {
      return delta(row) > 0 ? 'text-success' : (delta(row) < 0 ? 'text-danger' : 'text-muted')
    }

    const isOverLimit = (row: TraderRatesBulkRowDTO): boolean => {
      return row.warnings.some(warning => ['exceeds_team_lead_cap', 'exceeds_store_min'].includes(warning.code))
    }

    const warningHint = (code: string, limit: string | null): string => {
      return helper.trans(`rates_management.warning_hint.${code}`, {limit: limit ?? ''}).replace(/\s*\(%\)/, '')
    }

    return {
      helper,
      statusFilters,
      statusFilter,
      warningCodeFilter,
      search,
      subtitle,
      hasEstimates,
      filteredRows,
      toggleWarningFilter,
      chipClass,
      statusBadgeClass,
      deltaArrow,
      deltaClass,
      isOverLimit,
      warningHint,
    }
  }
})
</script>

<style>
.modal-dialog.rates-report-dialog {
  max-width: min(1320px, 96vw);
}
</style>
